import { useMemo } from 'react';

import { useCurrentUserRecord } from '@lib/auth/hooks/use-current-user-record';
import { joinFullName } from '@lib/shared/utils/join-full-name';
import { UserDisplayData } from '@lib/types/user-types';
import { formatAccountDate } from '@lib/utils/user-utils';

/**
 * Custom hook that provides standardized user display data
 * Eliminates duplication of user data processing logic across components
 */
export function useUserDisplay(): UserDisplayData {
  const { data: user, isLoading, error } = useCurrentUserRecord();

  return useMemo(() => {
    // Handle loading state
    if (isLoading) {
      return {
        userName: '',
        userEmail: '',
        avatarUrl: null,
        accountSince: '',
        isLoading: true,
        hasError: false,
      };
    }

    // Handle error or no user
    if (error || !user) {
      return {
        userName: 'User',
        userEmail: '',
        avatarUrl: null,
        accountSince: '',
        isLoading: false,
        hasError: true,
      };
    }

    // Extract user information.
    // `||` (not `??`) so empty strings fall through to the next
    // fallback — `users` columns are NOT NULL and default to ''.
    const userName =
      joinFullName(user.firstName, user.lastName) ||
      user.email?.split('@')[0] ||
      'User';
    const userEmail = user.email || '';
    const avatarUrl = user.avatarUrl || null;
    const accountSince = formatAccountDate(
      user.createdAt || new Date().toISOString()
    );

    return {
      userName,
      userEmail,
      avatarUrl,
      accountSince,
      isLoading: false,
      hasError: false,
    };
  }, [user, isLoading, error]);
}
